import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Shield, FileText, Clock, MapPin, Users, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Tables } from '@/integrations/supabase/types';

interface ConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  requiredPurposes: string[];
  onConsentComplete: (consentId: string) => void;
  onConsentDeclined: () => void;
  title?: string;
  description?: string;
}

interface ConsentFormData {
  selectedPurposes: string[];
  isExplicitConsent: boolean;
  understandsRights: boolean;
  agreesToProcessing: boolean;
  notes: string;
}

export const ConsentModal: React.FC<ConsentModalProps> = ({
  isOpen,
  onClose,
  requiredPurposes,
  onConsentComplete,
  onConsentDeclined,
  title = 'GDPR Consent for AI Clinical Features',
  description = 'Your explicit consent is required to use AI-powered clinical workflow enhancements.'
}) => {
  const { user } = useAuth();
  const [purposes, setPurposes] = useState<Tables<'data_processing_purposes'>[]>([]);
  const [formData, setFormData] = useState<ConsentFormData>({
    selectedPurposes: [],
    isExplicitConsent: false,
    understandsRights: false,
    agreesToProcessing: false,
    notes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadProcessingPurposes();
      resetForm();
    }
  }, [isOpen, requiredPurposes]);

  const loadProcessingPurposes = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('data_processing_purposes')
        .select('*')
        .in('purpose_code', requiredPurposes)
        .eq('is_active', true)
        .order('purpose_name');

      if (error) {
        console.error('Error loading processing purposes:', error);
        return;
      }

      setPurposes(data || []);
      // Pre-select all required purposes
      setFormData(prev => ({
        ...prev,
        selectedPurposes: requiredPurposes
      }));
    } catch (error) {
      console.error('Error loading processing purposes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      selectedPurposes: [],
      isExplicitConsent: false,
      understandsRights: false,
      agreesToProcessing: false,
      notes: ''
    });
  };

  const handlePurposeToggle = (purposeCode: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      selectedPurposes: checked
        ? [...prev.selectedPurposes, purposeCode]
        : prev.selectedPurposes.filter(p => p !== purposeCode)
    }));
  };

  const handleSubmitConsent = async () => {
    if (!user || !isFormValid()) return;

    try {
      setIsSubmitting(true);

      // Get user agent and IP info
      const userAgent = navigator.userAgent;
      
      // Create consent record - using only required fields
      const consentData = {
        patient_id: user.id,
        consent_method: 'online_form',
        purposes_consented: formData.selectedPurposes,
        is_active: true
      };

      const { data: consent, error } = await supabase
        .from('patient_consents')
        .insert(consentData)
        .select()
        .single();

      if (error) {
        console.error('Error creating consent record:', error);
        alert('Failed to save consent. Please try again.');
        return;
      }

      // Log the consent action in EU audit log
      await supabase.from('eu_audit_log').insert({
        user_id: user.id,
        patient_id: user.id,
        feature: 'gdpr_consent',
        action: 'consent_granted',
        input_data: {
          purposes: formData.selectedPurposes,
          consent_method: 'online_form'
        },
        output_data: {
          consent_id: consent.id,
          status: 'active'
        },
        legal_basis: 'consent',
        processing_purpose: 'consent_management',
        data_categories: ['consent_records', 'user_preferences'],
        processing_location: 'EU',
        session_id: crypto.randomUUID(),
        user_agent: userAgent
      });

      onConsentComplete(consent.id);
      onClose();
    } catch (error) {
      console.error('Error submitting consent:', error);
      alert('An error occurred while saving consent. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeclineConsent = async () => {
    if (!user) return;

    try {
      // Log the consent decline in EU audit log
      await supabase.from('eu_audit_log').insert({
        user_id: user.id,
        patient_id: user.id,
        feature: 'gdpr_consent',
        action: 'consent_declined',
        input_data: {
          purposes: requiredPurposes,
          decline_reason: 'user_choice'
        },
        legal_basis: 'legitimate_interests',
        processing_purpose: 'consent_management',
        data_categories: ['audit_logs'],
        processing_location: 'EU',
        session_id: crypto.randomUUID(),
        user_agent: navigator.userAgent
      });

      onConsentDeclined();
      onClose();
    } catch (error) {
      console.error('Error logging consent decline:', error);
      onConsentDeclined();
      onClose();
    }
  };

  const isFormValid = () => {
    return (
      formData.selectedPurposes.length > 0 &&
      formData.isExplicitConsent &&
      formData.understandsRights &&
      formData.agreesToProcessing
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <span>{title}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {description} This consent is required under GDPR Article 9 for processing health data.
            </AlertDescription>
          </Alert>

          {/* Processing Purposes Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>Data Processing Purposes</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoading ? (
                <div className="text-center py-4">Loading processing purposes...</div>
              ) : (
                purposes.map((purpose) => (
                  <div key={purpose.id} className="border rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id={purpose.purpose_code}
                        checked={formData.selectedPurposes.includes(purpose.purpose_code)}
                        onCheckedChange={(checked) => 
                          handlePurposeToggle(purpose.purpose_code, checked as boolean)
                        }
                        disabled={requiredPurposes.includes(purpose.purpose_code)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <label htmlFor={purpose.purpose_code} className="font-medium cursor-pointer">
                          {purpose.purpose_name}
                        </label>
                        <p className="text-sm text-gray-600 mt-1">{purpose.description}</p>
                        <div className="flex items-center space-x-2 mt-2">
                          <Badge variant="secondary" className="text-xs">
                            <Shield className="h-3 w-3 mr-1" />
                            {purpose.legal_basis}
                          </Badge>
                          {purpose.retention_period_days && (
                            <Badge variant="outline" className="text-xs">
                              <Clock className="h-3 w-3 mr-1" />
                              {Math.floor(purpose.retention_period_days / 365)} years
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs">
                            <MapPin className="h-3 w-3 mr-1" />
                            EU/EEA only
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* GDPR Rights Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center space-x-2">
                <Users className="h-5 w-5" />
                <span>Your Rights Under GDPR</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Right to Access (Article 15)</p>
                    <p className="text-gray-600">Request copies of your personal data</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Right to Rectification (Article 16)</p>
                    <p className="text-gray-600">Correct inaccurate personal data</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Right to Erasure (Article 17)</p>
                    <p className="text-gray-600">Request deletion of your data</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Right to Portability (Article 20)</p>
                    <p className="text-gray-600">Receive or transfer your data</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Right to Withdraw Consent (Article 7)</p>
                    <p className="text-gray-600">Withdraw consent at any time</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Right to Object (Article 21)</p>
                    <p className="text-gray-600">Object to certain processing</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Consent Confirmation Checkboxes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Consent Confirmation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="explicit-consent"
                    checked={formData.isExplicitConsent}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({ ...prev, isExplicitConsent: checked as boolean }))
                    }
                  />
                  <label htmlFor="explicit-consent" className="text-sm cursor-pointer">
                    I provide my <strong>explicit consent</strong> for the processing of my health data 
                    for the selected AI clinical workflow purposes in accordance with GDPR Article 9(2)(a).
                  </label>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="understands-rights"
                    checked={formData.understandsRights}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({ ...prev, understandsRights: checked as boolean }))
                    }
                  />
                  <label htmlFor="understands-rights" className="text-sm cursor-pointer">
                    I understand my rights under GDPR and that I can withdraw my consent 
                    at any time through the patient portal or by contacting support.
                  </label>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="agrees-processing"
                    checked={formData.agreesToProcessing}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({ ...prev, agreesToProcessing: checked as boolean }))
                    }
                  />
                  <label htmlFor="agrees-processing" className="text-sm cursor-pointer">
                    I agree to the processing of my data within EU/EEA regions only, 
                    with appropriate technical and organizational measures in place.
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Additional Notes (Optional)</label>
                <Textarea
                  placeholder="Any specific requirements or notes regarding your consent..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="min-h-[80px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-xs text-gray-500">
              Data Controller: ClinicFlow Healthcare Platform<br />
              Contact: support@clinicflow.com
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handleDeclineConsent}
                disabled={isSubmitting}
              >
                Decline Consent
              </Button>
              <Button
                onClick={handleSubmitConsent}
                disabled={!isFormValid() || isSubmitting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isSubmitting ? 'Saving...' : 'Provide Consent'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentModal;
